/**
 * V2 — Mapping rôle → workspace home et navigation simplifiée.
 *
 * Chaque rôle a une **maison** (l'écran sur lequel il atterrit en arrivant)
 * et un set d'écrans pertinents. La V2 fait le pari d'une navigation
 * radicalement spécialisée — pas de menu "tout pour tout le monde".
 */

import type { AppRole } from "@/contexts/RoleContext";
import type { LucideIcon } from "lucide-react";
import {
  Inbox,
  Kanban,
  LayoutDashboard,
  Newspaper,
  Megaphone,
  ClipboardList,
  Settings,
  Users,
  MapPin,
  Shield,
  Languages,
  Home,
  Mail,
  Upload,
  Wrench,
  SlidersHorizontal,
  RefreshCw,
} from "lucide-react";

export interface V2NavItem {
  to: string;
  /** Clé i18n du label (résolue via t() dans les renderers). */
  labelKey: string;
  icon: LucideIcon;
  /** Clé i18n du sous-label affiché sur mobile / version compacte. */
  shortKey?: string;
  /** Item grisé et non cliquable (feature à venir / chantier en pause). */
  disabled?: boolean;
  /** Clé i18n de l'en-tête de section affiché au-dessus de cet item, si
   *  différente de celle de l'item précédent (regroupement visuel dans la
   *  sidebar, ex. "Location" / "Vente" / "ELS"). */
  section?: string;
}

const COMMON_INBOX: V2NavItem = {
  to: "/v2/inbox",
  labelKey: "v2.nav.monInbox",
  icon: Inbox,
  shortKey: "v2.nav.short.inbox",
};

const BIENS: V2NavItem = {
  to: "/v2/biens",
  labelKey: "v2.nav.catalogueLocation",
  icon: Newspaper,
  shortKey: "v2.nav.short.biens",
};

/** Catalogue des biens en VENTE — page distincte du catalogue location.
 *  Même icône que BIENS (Catalogue Location) : c'est le même concept
 *  ("catalogue de biens"), juste un axe transaction différent — deux icônes
 *  différentes pour "Catalogue" donnait l'impression de deux fonctions
 *  distinctes (signalé Thibaud 2026-08-20). */
const BIENS_VENTE: V2NavItem = {
  to: "/v2/biens-vente",
  labelKey: "v2.nav.catalogueVente",
  icon: Newspaper,
  shortKey: "v2.nav.short.biensVente",
};

/** Workspace publication des biens en VENTE (publier/dépublier) — séparé du
 *  catalogue vente ET du workspace publication location (décision 2026-07-12).
 *  Même icône que le workspace Publication location (Megaphone, alignée sur
 *  l'onglet "Publication" de la fiche bien — demande Thibaud 2026-08-20) —
 *  même raison que BIENS_VENTE ci-dessus. */
const PUBLICATION_VENTE: V2NavItem = {
  to: "/v2/publication-vente",
  labelKey: "v2.nav.publicationVente",
  icon: Megaphone,
  shortKey: "v2.nav.short.publicationVente",
};

const APPARTS: V2NavItem = {
  to: "/v2/apparts-for-rent",
  labelKey: "nav.appartsForRent",
  icon: Home,
  shortKey: "v2.nav.short.appartsForRent",
};

export function getV2HomeForRole(role: AppRole): string {
  switch (role) {
    case "prestataire":
      return "/v2/missions";
    case "vente":
      // L'équipe Sales atterrit sur son catalogue — elle n'a ni dashboard
      // C-Level ni pipeline ELS (séparation vente/location, 2026-07-12).
      return "/v2/biens-vente";
    case "c_level":
      return "/v2/dashboard";
    case "publication":
      return "/v2/publication";
    case "admin":
      // Aucun rôle n'atterrit sur l'inbox au login (décision user 2026-06-07) :
      // admin → Dashboard. (L'inbox reste accessible via la nav admin.)
      return "/v2/dashboard";
    case "collaborateur":
    case "collaborateur_els":
    case "collaborateur_afr":
      // Inbox masquée pour le collaborateur (cf. getV2NavForRole) → page
      // d'accueil = Apparts for Rent (décision user 2026-08-18).
      return "/v2/apparts-for-rent";
    default:
      return "/v2/dashboard";
  }
}

// ── Garde-fou d'autorisation par rôle ────────────────────────────────────────
// Segments de 1er niveau (après /v2/) accessibles par rôle. 'admin' = tout.
// SÉCURITÉ : sans ça, n'importe quel connecté pouvait afficher /v2/dashboard
// (CLevelInsightsPage) ou /v2/admin/* — ex. un prestataire voyant les stats CLevel.
const V2_ALLOWED_SEGMENTS: Record<string, readonly string[]> = {
  prestataire: ["missions"],
  collaborateur: ["pipeline", "biens", "apparts-for-rent", "inbox"],
  // Équipe Sales : uniquement son catalogue + son workspace publication.
  // Pas de dashboard, pas de pipeline ELS, pas de catalogue location.
  vente: ["biens-vente", "publication-vente"],
  publication: ["publication", "biens"],
  c_level: ["dashboard", "insights", "biens", "biens-vente", "apparts-for-rent", "pipeline", "publication", "publication-vente", "missions", "immoweb-sync"],
};

function baseRoleKey(role: AppRole): string {
  if (role.startsWith("collaborateur")) return "collaborateur";
  if (role.startsWith("publication")) return "publication";
  return role;
}

/** True si le rôle a le droit d'afficher cette route /v2/*. */
export function canAccessV2Path(role: AppRole, pathname: string): boolean {
  if (role === "admin") return true;
  const parts = pathname.replace(/^\/v2\/?/, "").split("/"); // ['biens','123'] | ['dashboard'] | ['']
  const seg = parts[0]; // '' | 'missions' | 'biens' | 'dashboard' | 'admin' ...
  if (seg === "") return true; // home (/v2) → redirigé ailleurs
  // Équipe Sales : la FICHE d'un bien vit sous /v2/biens/:id (bifurcation
  // vente/location dans V2BienDetailWrapper) → autoriser la fiche (id présent)
  // SANS ouvrir la liste location (/v2/biens seul) ni le formulaire de
  // création location (/v2/biens/new). Les field_permissions restent le
  // garde-fou données si l'id tapé est un bien location.
  if (baseRoleKey(role) === "vente" && seg === "biens" && !!parts[1] && parts[1] !== "new") {
    return true;
  }
  const allowed = V2_ALLOWED_SEGMENTS[baseRoleKey(role)];
  if (!allowed) return false; // rôle inconnu → tout bloquer (sécurité par défaut)
  return allowed.includes(seg);
}

export function getV2NavForRole(role: AppRole): V2NavItem[] {
  switch (role) {
    case "collaborateur":
    case "collaborateur_els":
    case "collaborateur_afr":
      // "Mon inbox" masquée pour le collaborateur (décision user 2026-06-02) :
      // son flux passe par le Pipeline ELS + Apartments for Rent. La route
      // /v2/inbox reste accessible en tapant l'URL directement.
      // Apparts for Rent en premier (décision user 2026-08-18) : c'est la
      // page d'accueil du rôle, cf. getV2HomeForRole.
      return [
        APPARTS,
        { to: "/v2/pipeline", labelKey: "v2.nav.pipelineEls", icon: Kanban, shortKey: "v2.nav.short.pipeline" },
      ];
    case "vente":
      // Équipe Sales : catalogue vente + publication vente, rien d'autre
      // (séparation vente/location, décision 2026-07-12).
      return [BIENS_VENTE, PUBLICATION_VENTE];
    case "c_level":
      // Inbox masquée pour le CLevel : ses alertes sont entièrement
      // représentées dans les tuiles du Dashboard (À débloquer, À compléter,
      // buckets dispo, PEB absent) + le mini-dashboard ELS par collab.
      // La route /v2/inbox reste accessible si on tape l'URL directement.
      //
      // Publication + Prestataires sont en "disabled" tant qu'on n'a pas
      // fini la revue UX de ces 2 workspaces (chantier en pause).
      // Sidebar regroupée par domaine (décision user 2026-08-20) : Location /
      // Vente / ELS, avec des libellés courts dédiés à ce rôle (les autres
      // rôles gardent leurs libellés longs habituels, ex. "Catalogue Location").
      return [
        { to: "/v2/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, shortKey: "v2.nav.short.dashboard" },
        { ...BIENS, labelKey: "v2.nav.catalogue", section: "v2.nav.section.location" },
        // Icône dédiée (enveloppe) pour ce libellé C-Level uniquement — la
        // page "Apparts for Rent" du collaborateur (même route, même item de
        // base APPARTS) garde son icône par défaut (demande Thibaud
        // 2026-08-20, après 2 essais : parlophone puis haut-parleur — "mets
        // une enveloppe").
        { ...APPARTS, labelKey: "nav.mailing", icon: Mail, section: "v2.nav.section.location" },
        { to: "/v2/publication", labelKey: "v2.nav.publication", icon: Megaphone, shortKey: "v2.nav.short.publication", section: "v2.nav.section.location" },
        { ...BIENS_VENTE, labelKey: "v2.nav.catalogue", section: "v2.nav.section.vente" },
        { ...PUBLICATION_VENTE, labelKey: "v2.nav.publication", section: "v2.nav.section.vente" },
        { to: "/v2/pipeline", labelKey: "v2.nav.pipelineSkyline", icon: Kanban, shortKey: "v2.nav.short.pipeline", section: "v2.nav.section.els" },
        { to: "/v2/missions", labelKey: "nav.prestataires", icon: ClipboardList, shortKey: "v2.nav.short.prestataires", section: "v2.nav.section.els" },
        // Synchronisation Immoweb → catalogue : réservée au C-Level (demande
        // Thibaud 2026-10-19), contrôlée aussi par l'Edge Function et la RLS.
        { to: "/v2/immoweb-sync", labelKey: "v2.nav.immowebSync", icon: RefreshCw, shortKey: "v2.nav.short.immowebSync", section: "v2.nav.section.outils" },
      ];
    case "publication":
      // Inbox grisé : pas encore d'usage métier pour la Publication (les
      // tâches sont visibles dans /v2/publication "À préparer"). Apparts
      // for Rent masqué : strictement orthogonal à la mission de publi.
      return [
        { ...COMMON_INBOX, disabled: true },
        { to: "/v2/publication", labelKey: "v2.nav.publication", icon: Megaphone, shortKey: "v2.nav.short.publication" },
      ];
    case "prestataire":
      return [
        { to: "/v2/missions", labelKey: "v2.nav.mesMissions", icon: ClipboardList, shortKey: "v2.nav.short.missions" },
      ];
    case "admin":
      return [
        COMMON_INBOX,
        { to: "/v2/dashboard", labelKey: "nav.dashboard", icon: LayoutDashboard, shortKey: "v2.nav.short.dashboard" },
        BIENS,
        BIENS_VENTE,
        APPARTS,
        { to: "/v2/pipeline", labelKey: "v2.nav.pipelineEls", icon: Kanban, shortKey: "v2.nav.short.pipeline" },
        { to: "/v2/publication", labelKey: "v2.nav.publication", icon: Megaphone, shortKey: "v2.nav.short.publication" },
        PUBLICATION_VENTE,
        { to: "/v2/missions", labelKey: "v2.nav.missions", icon: ClipboardList, shortKey: "v2.nav.short.missions" },
        { to: "/v2/interventions", labelKey: "nav.interventions", icon: Wrench, shortKey: "v2.nav.short.interventions" },
        { to: "/v2/admin/dashboard", labelKey: "v2.nav.dashboardClassique", icon: LayoutDashboard, shortKey: "v2.nav.short.dashboardClassique" },
        { to: "/v2/admin/equipe", labelKey: "nav.equipe", icon: Users, shortKey: "v2.nav.short.equipe" },
        // Deux entrées : les deux fiches partagent la table `field_permissions`
        // mais pas leurs champs. Une bascule enfouie dans la page laisserait
        // croire qu'on règle « les droits » alors qu'on n'en règle qu'une moitié.
        { to: "/v2/admin/droits-champs", labelKey: "nav.droitsLocation", icon: Shield, shortKey: "v2.nav.short.droitsLocation" },
        { to: "/v2/admin/droits-vente", labelKey: "nav.droitsVente", icon: Shield, shortKey: "v2.nav.short.droitsVente" },
        { to: "/v2/admin/quartiers", labelKey: "nav.quartiers", icon: MapPin, shortKey: "v2.nav.short.quartiers" },
        { to: "/v2/admin/metiers", labelKey: "nav.metiers", icon: Settings, shortKey: "v2.nav.short.metiers" },
        { to: "/v2/admin/traductions", labelKey: "nav.traductions", icon: Languages, shortKey: "v2.nav.short.traductions" },
        { to: "/v2/admin/administration", labelKey: "nav.administration", icon: SlidersHorizontal, shortKey: "v2.nav.short.administration" },
        { to: "/v2/admin/import", labelKey: "v2.nav.importRentio", icon: Upload, shortKey: "v2.nav.short.importRentio" },
      ];
    default:
      return [COMMON_INBOX];
  }
}
